import { useCurrentWorkspace } from '../../auth';
import { useFindByQuery, useCustomQuery } from '../../graphql';

export const useEntityAlerts = (
  entityType: 'group' | 'product' | 'asset',
  entityId: string
) => {
  const workspace = useCurrentWorkspace(true);

  // For assets, we need to get the workspaceAsset ID first
  const workspaceAssetFilter =
    entityType === 'asset' && entityId
      ? {
          asset: { eq: entityId },
          ...(workspace?.id ? { workspace: { eq: workspace.id } } : {}),
        }
      : undefined;

  const { data: workspaceAssetData, isLoading: isWorkspaceAssetLoading } =
    useCustomQuery({
      cacheKey: ['workspaceAssetForAlerts', entityType, entityId, workspace?.id],
      query: FIND_WORKSPACE_ASSET_FOR_ALERTS,
      variables: {
        filter: workspaceAssetFilter,
      },
      options: {
        enabled:
          entityType === 'asset' &&
          !!entityId &&
          !!workspaceAssetFilter &&
          !!workspace?.id,
      },
    });

  // Get workspaceAsset ID for assets
  const workspaceAssetId =
    entityType === 'asset'
      ? workspaceAssetData?.findWorkspaceAssets?.items?.[0]?.id
      : entityId;

  const filter: any = {
    action: { type: { eq: 'ALERT' } },
    disabled: { ne: true },
  };

  if (entityType === 'group') {
    filter.group = { eq: entityId };
  } else if (entityType === 'product') {
    filter.product = { eq: entityId };
  } else if (entityType === 'asset') {
    filter.workspaceAsset = { eq: workspaceAssetId };
  }

  const { data, isLoading, refetch } = useFindByQuery({
    modelName: 'workflow',
    query: FIND_ENTITY_WORKFLOWS,
    variables: {
      filter,
      page: { limit: 10000, offset: 0 },
    },
    options: {
      enabled:
        !!workspaceAssetId &&
        (entityType !== 'asset' || !isWorkspaceAssetLoading),
    },
  });

  return {
    workflows: data?.items || [],
    isLoading: isLoading || isWorkspaceAssetLoading,
    refetch,
    /** Resolved workspaceAsset id when entityType is 'asset'; use this for getWorkspaceAsset/edit flows */
    workspaceAssetId: entityType === 'asset' ? workspaceAssetId : undefined,
  };
};

const FIND_WORKSPACE_ASSET_FOR_ALERTS = `
  query FindWorkspaceAssetForAlerts($filter: WorkspaceAssetFilter) {
    findWorkspaceAssets(filter: $filter) {
      items {
        id
      }
    }
  }
`;

const FIND_ENTITY_WORKFLOWS = `
  query FindEntityWorkflows($filter: WorkflowFilter, $page: PageRequest) {
    findWorkflows(filter: $filter, page: $page) {
      items {
        id
        name
        trigger {
          type
          config
        }
        action {
          type
          config
        }
        disabled
        createdAt
      }
    }
  }
`;
